import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import attendanceService from '../../../services/attendanceService';

const PunchAttendanceModal = ({
  isOpen,
  onClose,
  employee,
  onSaved
}) => {
  const [date, setDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [status, setStatus] = useState('present');
  const [checkInTime, setCheckInTime] = useState('09:00');
  const [checkOutTime, setCheckOutTime] = useState('18:00');
  const [workHours, setWorkHours] = useState('8.0');
  const [remarks, setRemarks] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (employee && isOpen) {
      const todayStr = new Date().toISOString().split('T')[0];
      setDate(todayStr);
      setStatus('present');
      setCheckInTime('09:00');
      setCheckOutTime('18:00');
      setWorkHours('8.0');
      setRemarks('');
      setError(null);
    }
  }, [employee, isOpen]);

  if (!isOpen || !employee) return null;

  const statusOptions = [
    { value: 'present', label: 'Present' },
    { value: 'late', label: 'Late Arrival' },
    { value: 'half_day', label: 'Half Day (HD)' },
    { value: 'absent', label: 'Absent' },
    { value: 'leave', label: 'Leave (L)' },
    { value: 'paid_leave', label: 'Paid Leave (PL)' },
    { value: 'week_off', label: 'Week Off (WO)' }
  ];

  const getCurrentTimeString = () => {
    const now = new Date();
    const hrs = String(now.getHours()).padStart(2, '0');
    const mins = String(now.getMinutes()).padStart(2, '0');
    return `${hrs}:${mins}`;
  };

  const handleQuickPunchIn = () => {
    const nowTime = getCurrentTimeString();
    setCheckInTime(nowTime);
    setStatus('present');
    const todayStr = new Date().toISOString().split('T')[0];
    setDate(todayStr);
    setRemarks(`Quick Punch-In at ${nowTime}`);
  };

  const handleQuickPunchOut = () => {
    const nowTime = getCurrentTimeString();
    setCheckOutTime(nowTime);
    const todayStr = new Date().toISOString().split('T')[0];
    setDate(todayStr);
    setRemarks(prev => prev ? `${prev} | Punch-Out at ${nowTime}` : `Quick Punch-Out at ${nowTime}`);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError(null);

      const targetStatus = typeof status === 'object' ? status?.value || status?.target?.value : status;

      const payload = {
        employee_id: employee.id,
        date: date,
        status: targetStatus,
        check_in_time: (targetStatus === 'present' || targetStatus === 'late' || targetStatus === 'half_day') ? checkInTime : null,
        check_out_time: (targetStatus === 'present' || targetStatus === 'late' || targetStatus === 'half_day') ? checkOutTime : null,
        worked_hours: (targetStatus === 'present' || targetStatus === 'late' || targetStatus === 'half_day') ? parseFloat(workHours || 0) : null,
        remarks: remarks || 'Manual punch entry'
      };

      const res = await attendanceService.saveAttendanceRecord(payload);
      if (res.success) {
        if (onSaved) onSaved(res.data);
        onClose();
      } else {
        setError(res.error || 'Failed to record attendance punch.');
      }
    } catch (err) {
      setError(err.message || 'Error recording punch entry.');
    } finally {
      setSaving(false);
    }
  };

  const initials = (employee.full_name || 'EMP')
    .split(' ')
    .map(p => p[0])
    .join('')
    .toUpperCase()
    .substring(0, 2);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4 overflow-y-auto">
      <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Modal Header */}
        <div className="p-5 border-b border-border bg-muted/30 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 text-primary font-bold flex items-center justify-center text-sm shrink-0 border border-primary/20 overflow-hidden">
              {employee.face_photo_url ? (
                <img src={employee.face_photo_url} alt={employee.full_name} className="w-full h-full object-cover" />
              ) : (
                initials
              )}
            </div>
            <div>
              <h3 className="text-base font-bold text-foreground">Mark Attendance Punch</h3>
              <p className="text-xs text-muted-foreground">
                {employee.full_name} <span className="font-mono text-primary">({employee.employee_code || 'N/A'})</span>
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground p-1.5 rounded-lg hover:bg-muted transition-colors"
          >
            <Icon name="X" size={18} />
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSave} className="p-5 space-y-4 text-sm">
          {error && (
            <div className="p-3 bg-rose-500/10 border border-rose-500/20 text-rose-600 rounded-md text-xs font-medium">
              {error}
            </div>
          )}

          {/* Quick Punch Action Buttons */}
          <div className="grid grid-cols-2 gap-3 p-3 bg-muted/40 rounded-lg border border-border/60">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleQuickPunchIn}
              className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20 hover:bg-emerald-500/20 font-semibold"
              iconName="LogIn"
              iconPosition="left"
            >
              Punch In Now
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleQuickPunchOut}
              className="bg-rose-500/10 text-rose-600 border-rose-500/20 hover:bg-rose-500/20 font-semibold"
              iconName="LogOut"
              iconPosition="left"
            >
              Punch Out Now
            </Button>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-foreground mb-1.5">Date</label>
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground mb-1.5">Status</label>
              <Select
                options={statusOptions}
                value={status}
                onChange={(val) => setStatus(typeof val === 'object' ? val?.target?.value || val?.value || 'present' : val)}
              />
            </div>
          </div>

          {(status === 'present' || status === 'late' || status === 'half_day') && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-foreground mb-1.5">Check-In Time</label>
                  <Input
                    type="time"
                    value={checkInTime}
                    onChange={(e) => setCheckInTime(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground mb-1.5">Check-Out Time</label>
                  <Input
                    type="time"
                    value={checkOutTime}
                    onChange={(e) => setCheckOutTime(e.target.value)}
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground mb-1.5">Worked Hours (DECIMAL)</label>
                <Input
                  type="number"
                  step="0.25"
                  min="0"
                  max="24"
                  value={workHours}
                  onChange={(e) => setWorkHours(e.target.value)}
                />
              </div>
            </>
          )}

          <div>
            <label className="block text-xs font-medium text-foreground mb-1.5">Notes / Remarks</label>
            <Input
              type="text"
              placeholder="e.g. Manual Punch Entry by Admin"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
            />
          </div>

          {/* Action Buttons */}
          <div className="pt-3 border-t border-border flex items-center justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="bg-teal-600 hover:bg-teal-700 text-white font-semibold"
              iconName={saving ? "Loader2" : "CheckCircle"}
              iconPosition="left"
            >
              {saving ? "Saving Punch..." : "Submit Punch Entry"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PunchAttendanceModal;
